import React from 'react';
import NProgress from 'nprogress';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import { DocumentForm } from '../shared';
import { DocumentModel, User, Multimedia, Company } from '../../models';
import { useAlgolia, useNotify, useUser } from '../../hooks';
import { CREATE_USER, UPDATE_USER } from '../../graphql/mutation';
import {
  validateEmployeeInfoForm,
  validateUserForm,
} from '../../lib/formValidations';
import { reducer, UserFormFields } from '../shared/Forms/GeneralUserFormFields';
import { roles } from '../../static';
import { FormButtons } from '../shared/Forms';
import {
  EmployeeInfoForm,
  reducer as EmployeeInfoReducer,
} from '../shared/Forms/EmployeeInfoForm';

interface UserFormProps {
  user?: User;
  company?: Company;
}

function UserForm({ user, company }: UserFormProps) {
  const notify = useNotify();
  const router = useRouter();
  const [currentUser] = useUser();
  const [disabled, setDisabled] = React.useState(false);

  const [documents, setDocuments] = React.useState<
    Array<Partial<DocumentModel>>
  >(
    (user?.photo as Multimedia)?.src
      ? [{ src: (user?.photo as Multimedia)?.src }]
      : []
  );
  const [state, dispatch] = React.useReducer(
    reducer,
    user
      ? { ...user }
      : {
          dniType: 'V',
          privilege: 0,
          userType:
            currentUser.userType === roles.admin ? roles.admin : roles.client,
        }
  );

  const [employeeInfo, dispatchEmployeeInfo] = React.useReducer(
    EmployeeInfoReducer,
    user?.client?.employeeInfo ?? {}
  );

  const [updateUserMutation] = useMutation<{
    updateUser: User;
  }>(UPDATE_USER);

  const [createUserMutation] = useMutation<{
    createUser: User;
  }>(CREATE_USER);

  const { clearCache } = useAlgolia();

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    clearCache();
    try {
      e.preventDefault();
      NProgress.start();
      // disabled validation
      if (disabled) return;
      setDisabled(true);
      // validate form
      const error = validateUserForm({
        user: { ...state },
        update: !!user?._id,
      });
      if (error) {
        notify(error, 'error');
        return;
      }

      if (
        user?.userType === roles.client ||
        currentUser.userType !== roles.admin
      ) {
        const employeeInfoError = validateEmployeeInfoForm(employeeInfo);

        if (employeeInfoError) {
          notify(employeeInfoError, 'error');
          return;
        }
      }

      const userData = {
        ...state,
      } as User;
      // create or update user
      if (user) {
        delete userData.company;
        await updateUser({
          ...userData,
        });
      } else {
        await createUser(userData);
      }
    } catch (error) {
      // error duplicate email
      if (error.message.includes('dup key: { email')) {
        return notify('El correo ya se encuentra registrado', 'error');
      }
      // error duplicate dni
      if (error.message.includes('dup key: { dni')) {
        return notify('El DNI ya se encuentra registrado', 'error');
      }
      notify(error.message, 'error');
    } finally {
      NProgress.done();
      setDisabled(false);
    }
  };

  const createUser = async (userData: Partial<User>) => {
    clearCache();
    const { data } = await createUserMutation({
      variables: {
        data: {
          ...userData,
          photo: (documents[0] as Multimedia)?.src ?? null,

          companyId:
            currentUser.userType === roles.company
              ? (currentUser.company as Company)._id
              : null,
          ...(userData.userType === roles.client
            ? {
                employeeInfo: {
                  ...employeeInfo,
                },
              }
            : {}),
        },
      },
    });
    if (data?.createUser?._id) {
      notify('Usuario creado con éxito', 'success');
      const path = `/app/${
        currentUser.userType === roles.admin ? 'users' : 'employees'
      }`;
      router.push(path);
    } else {
      notify('Ha ocurrido un error al crear el usuario', 'error');
    }
  };

  const updateUser = async (userData: Partial<User>) => {
    clearCache();
    delete userData._id;
    delete userData.firstLogin;
    delete userData.client;
    delete userData.currentSubscription;
    delete userData.discountCoupons;

    const { data } = await updateUserMutation({
      variables: {
        data: {
          ...userData,
          _id: user?._id,
          company: {
            _id: (user?.company as Company)?._id ?? null,
            __typename: undefined,
          },
          photo: {
            src: (documents[0] as Multimedia)?.src ?? null,
            __typename: undefined,
          },
          ...(userData.userType === roles.client
            ? {
                employeeInfo: {
                  ...employeeInfo,
                  __typename: undefined,
                },
              }
            : {}),
          __typename: undefined,
        },
      },
    });
    if (data?.updateUser?._id) {
      notify('Usuario actualizado con éxito', 'success');

      const path = `/app/${
        currentUser.userType === roles.admin ? 'users' : 'employees'
      }`;

      if (router.pathname.includes('clients')) {
        router.push(`/app/clients`);
        return;
      }

      router.push(path);
    } else {
      notify('Ha ocurrido un error al actualizar el usuario', 'error');
    }
  };

  const changeUserStatus = async () => {
    await updateUser({
      active: !state.active,
    });
  };

  return (
    <form
      method="POST"
      onSubmit={onSubmit}
      className="flex flex-col w-full space-y-10"
    >
      <UserFormFields
        dispatch={dispatch}
        state={state}
        userType={roles.client}
        header={{
          title: !state._id ? 'Crear usuario' : 'Editar usuario',
        }}
      />
      {user?.userType === roles.client ||
      currentUser.userType !== roles.admin ? (
        <EmployeeInfoForm
          dispatch={dispatchEmployeeInfo}
          state={employeeInfo}
          companyInfo={company?.companyDetails}
          header={{
            title: 'Información del beneficiario',
          }}
        />
      ) : null}
      <DocumentForm
        documents={documents}
        updateURLs={setDocuments}
        maximumAmount={1}
      />
      <FormButtons
        type={user ? 'update' : 'create'}
        toggleActive={changeUserStatus}
        active={user?.active}
        disabled={disabled}
        onSubmit={onSubmit}
      />
    </form>
  );
}

export default UserForm;
